import { SecaoCard } from "@/components/padroes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCents, formatDateTime } from "@/lib/format";
import type { LancamentosErp, StatusLancamentoErp } from "@/data/erp";
import { AlertTriangle, Loader2, Send } from "lucide-react";

const STATUS: Record<StatusLancamentoErp, { rotulo: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  criado: { rotulo: "Lançado", variant: "default" },
  existente: { rotulo: "Já lançado", variant: "secondary" },
  divergente: { rotulo: "Valor divergente", variant: "destructive" },
  erro: { rotulo: "Erro", variant: "destructive" },
};

const mascaraDocumento = (d: string | null) => {
  if (!d) return "—";
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  return d;
};

interface Props {
  titulo: string;
  dados: LancamentosErp | null;
  /** Rótulo da coluna do favorecido: "Colaborador" na folha, "Empresa" nos benefícios. */
  rotuloNome: string;
  mostrarTipo?: boolean;
  textoVazio: string;
  /** Sem `onEnviar`, só visualização (Financeiro). */
  onEnviar?: () => void;
  rotuloEnviar?: string;
  enviando?: boolean;
}

export const ErpLancamentosPainel = ({
  titulo,
  dados,
  rotuloNome,
  mostrarTipo = false,
  textoVazio,
  onEnviar,
  rotuloEnviar = "Reenviar ao ERP",
  enviando = false,
}: Props) => {
  const lancamentos = dados?.lancamentos ?? [];
  const resumo = dados?.resumo;
  const totalNoErp = lancamentos
    .filter((l) => l.contasPagarId)
    .reduce((s, l) => s + l.valor, 0);

  return (
    <SecaoCard
      semPadding
      titulo={titulo}
      descricao={
        resumo && resumo.total > 0 ? (
          <>
            {resumo.criados + resumo.existentes + resumo.divergentes} de {resumo.total} com conta a pagar
            {" · "}
            {formatCents(totalNoErp)} no ERP
            {resumo.ultimoEnvio && <> · último envio em {formatDateTime(resumo.ultimoEnvio)}</>}
          </>
        ) : (
          textoVazio
        )
      }
      acoes={
        onEnviar && (
          <Button size="sm" variant="outline" onClick={onEnviar} disabled={enviando}>
            {enviando ? <Loader2 className="animate-spin" /> : <Send />}
            {rotuloEnviar}
          </Button>
        )
      }
    >
        {(dados?.avisosLote.length ?? 0) > 0 && (
          <div className="mx-5 my-4 rounded-[10px] border border-amber-500/30 bg-amber-500/[.07] px-4 py-3 text-[13px] text-amber-800 dark:text-amber-300 sm:mx-6">
            {dados!.avisosLote.map((a) => (
              <p key={a}>{a}</p>
            ))}
          </div>
        )}

        {lancamentos.length > 0 && (
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5 sm:pl-6">{rotuloNome}</TableHead>
                {mostrarTipo && <TableHead>Tipo</TableHead>}
                <TableHead>Documento</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Conta a pagar</TableHead>
                <TableHead>Observações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lancamentos.map((l) => {
                const status = STATUS[l.status] ?? STATUS.erro;
                const observacoes = [...(l.erro ? [l.erro] : []), ...(l.avisos ?? [])];
                return (
                  <TableRow key={l.id}>
                    <TableCell className="pl-5 font-medium sm:pl-6">{l.nome}</TableCell>
                    {mostrarTipo && <TableCell>{l.tipoContrato ?? "—"}</TableCell>}
                    <TableCell className="whitespace-nowrap text-xs">{mascaraDocumento(l.documento)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">{formatCents(l.valor)}</TableCell>
                    <TableCell>
                      <Badge variant={status.variant}>{status.rotulo}</Badge>
                    </TableCell>
                    <TableCell>{l.contasPagarId ?? "—"}</TableCell>
                    <TableCell className="max-w-md text-xs">
                      {observacoes.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <ul className="space-y-1">
                          {observacoes.map((o) => (
                            <li key={o} className="flex gap-1.5">
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
                              <span>{o}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
    </SecaoCard>
  );
};
